import { PayloadAction } from "@reduxjs/toolkit";

import { AutocompleteItem } from "../../../../components/Autocomplete";
import { ResetApplicantIsOwner } from "../../../../components/RewriteOwnerDataByApplicantData/ResetApplicantIsOwner";
import { DownloadApplicantDataFromStorageExport } from "../../../../components/RewriteOwnerDataByApplicantData/RewriteOwnerDataByApplicantData";
import { FormId } from "../../../../components/enums";
import { defaultCountry } from "../../../../shared";
import {
  createGenericUISlice,
  GenericState,
  UIState,
} from "../../../../store/HOC/genericUI.slice";
import { generateFormattedDate } from "../../../../utilities/functions";
import { ComponentId as ApplicantId } from ".././Introductory/ApplicantData/enums";

import { ComponentId as CompanyComponentId } from "./CompanyData/enums";
import { ComponentId as ContactPersonComponentId } from "./ContactPersonData/enums";
import { ComponentId as OwnerComponentId } from "./OwnerData/enums";
import { ComponentId as PersonComponentId } from "./PersonData/enums";
import { ComponentId as VehicleComponentId } from "./VehicleData/enums";

export type FormDataInputs = {
  [FormId.VEHICLE_OWNER_VEHICLE]: {
    [VehicleComponentId.VEHICLE_TYPE]: string;
    [VehicleComponentId.REGISTRATION_NUMBER]: string;
    [VehicleComponentId.VIN]: string;
    [VehicleComponentId.PRODUCTION_YEAR]: string;
    [VehicleComponentId.MAKE]: string;
    [VehicleComponentId.MODEL]: string;
    [VehicleComponentId.REGISTRATION_DATE]: string;
    [VehicleComponentId.DESCRIPTION]: string;
  };
  [FormId.VEHICLE_OWNER_OWNER]: {
    [OwnerComponentId.OWNER_TYPE]: string;
  };
  [FormId.VEHICLE_OWNER_COMPANY_DATA]: {
    [CompanyComponentId.NAME]: string;
    [CompanyComponentId.REGON]: string;
    [CompanyComponentId.PHONE]: string;
    [CompanyComponentId.EMAIL]: string;
    [CompanyComponentId.STREET]: string;
    [CompanyComponentId.STREET_2]: string;
    [CompanyComponentId.ZIP_CODE_PL]: string;
    [CompanyComponentId.ZIP_CODE_GENERIC]: string;
    [CompanyComponentId.CITY]: string;
    [CompanyComponentId.COUNTRY]: AutocompleteItem;
    [CompanyComponentId.PROVINCE]: string;
  };
  //JIRA LPPLWEBMOB-495
  [FormId.VEHICLE_OWNER_PERSON_DATA]: {
    [PersonComponentId.APPLICANT_IS_OWNER]: string | boolean;
    [PersonComponentId.NAME]: string;
    [PersonComponentId.SURNAME]: string;
    [PersonComponentId.PESEL]: string;
    [PersonComponentId.PERSON_DATA_LACK_OF_PESEL]: string | boolean;
    [PersonComponentId.PHONE]: string;
    [PersonComponentId.EMAIL]: string;
    [PersonComponentId.STREET]: string;
    [PersonComponentId.STREET_2]: string;
    [PersonComponentId.ZIP_CODE_PL]: string;
    [PersonComponentId.ZIP_CODE_GENERIC]: string;
    [PersonComponentId.CITY]: string;
    [PersonComponentId.COUNTRY]: AutocompleteItem;
    [PersonComponentId.PROVINCE]: string;
    [PersonComponentId.COOWNER]: string;
  };
  [FormId.INTRODUCTORY_APPLICANT]: {
    [ApplicantId.APPLICANT_NAME]: string;
    [ApplicantId.APPLICANT_SURNAME]: string;
    [ApplicantId.APPLICANT_PHONE]: string;
    [ApplicantId.APPLICANT_EMAIL]: string;
  };
  [FormId.VEHICLE_OWNER_CONTACT_PERSON_DATA]: {
    [ContactPersonComponentId.PERSON_TYPE]: string;
    [ContactPersonComponentId.NAME]: string;
    [ContactPersonComponentId.SURNAME]: string;
    [ContactPersonComponentId.COMPANY_NAME]: string;
    [ContactPersonComponentId.PHONE]: string;
    [ContactPersonComponentId.EMAIL]: string;
  };
};

export type VehicleOwnerVehicleFormInputs = FormDataInputs[FormId.VEHICLE_OWNER_VEHICLE];
export type VehicleOwnerOwnerFormInputs = FormDataInputs[FormId.VEHICLE_OWNER_OWNER];
export type VehicleOwnerCompanyFormInputs = FormDataInputs[FormId.VEHICLE_OWNER_COMPANY_DATA];
export type VehicleOwnerPersonFormInputs = FormDataInputs[FormId.VEHICLE_OWNER_PERSON_DATA];
export type VehicleOwnerContactPersonFormInputs = FormDataInputs[FormId.VEHICLE_OWNER_CONTACT_PERSON_DATA];

type VehicleOwnerState = {
  data: FormDataInputs;
  ui: UIState;
};

export const initialState: VehicleOwnerState = {
  data: {
    [FormId.VEHICLE_OWNER_VEHICLE]: {
      [VehicleComponentId.VEHICLE_TYPE]: "",
      [VehicleComponentId.REGISTRATION_NUMBER]: "",
      [VehicleComponentId.VIN]: "",
      [VehicleComponentId.PRODUCTION_YEAR]: generateFormattedDate(),
      [VehicleComponentId.MAKE]: "",
      [VehicleComponentId.MODEL]: "",
      [VehicleComponentId.REGISTRATION_DATE]: "",
      [VehicleComponentId.DESCRIPTION]: "",
    },
    [FormId.VEHICLE_OWNER_OWNER]: {
      [OwnerComponentId.OWNER_TYPE]: "",
    },
    [FormId.VEHICLE_OWNER_COMPANY_DATA]: {
      [CompanyComponentId.NAME]: "",
      [CompanyComponentId.REGON]: "",
      [CompanyComponentId.PHONE]: "",
      [CompanyComponentId.EMAIL]: "",
      [CompanyComponentId.STREET]: "",
      [CompanyComponentId.STREET_2]: "",
      [CompanyComponentId.ZIP_CODE_PL]: "",
      [CompanyComponentId.ZIP_CODE_GENERIC]: "",
      [CompanyComponentId.CITY]: "",
      [CompanyComponentId.COUNTRY]: {
        ...defaultCountry,
      },
      [CompanyComponentId.PROVINCE]: "",
    },
    [FormId.VEHICLE_OWNER_PERSON_DATA]: {
      [PersonComponentId.APPLICANT_IS_OWNER]: false,
      [PersonComponentId.NAME]: "",
      [PersonComponentId.SURNAME]: "",
      [PersonComponentId.PESEL]: "",
      [PersonComponentId.PERSON_DATA_LACK_OF_PESEL]: false,
      [PersonComponentId.PHONE]: "",
      [PersonComponentId.EMAIL]: "",
      [PersonComponentId.STREET]: "",
      [PersonComponentId.STREET_2]: "",
      [PersonComponentId.ZIP_CODE_PL]: "",
      [PersonComponentId.ZIP_CODE_GENERIC]: "",
      [PersonComponentId.CITY]: "",
      [PersonComponentId.COUNTRY]: {
        ...defaultCountry,
      },
      [PersonComponentId.PROVINCE]: "",
      [PersonComponentId.COOWNER]: "",
    },
    [FormId.VEHICLE_OWNER_CONTACT_PERSON_DATA]: {
      [ContactPersonComponentId.PERSON_TYPE]: "",
      [ContactPersonComponentId.NAME]: "",
      [ContactPersonComponentId.SURNAME]: "",
      [ContactPersonComponentId.COMPANY_NAME]: "",
      [ContactPersonComponentId.PHONE]: "",
      [ContactPersonComponentId.EMAIL]: "",
    },
    [FormId.INTRODUCTORY_APPLICANT]: {
      [ApplicantId.APPLICANT_NAME]: "",
      [ApplicantId.APPLICANT_SURNAME]: "",
      [ApplicantId.APPLICANT_PHONE]: "",
      [ApplicantId.APPLICANT_EMAIL]: "",
    },
  },
  ui: {
    validated: false,
    "ownerCompany[country]": defaultCountry.value,
    "ownerPerson[country]": defaultCountry.value,
  },
};

const resetPeselData = (payload: FormDataInputs) => {
  const field = payload[FormId.VEHICLE_OWNER_PERSON_DATA];
  const subField =
    field !== undefined
      ? field[PersonComponentId.PERSON_DATA_LACK_OF_PESEL]
      : undefined;

  if (subField !== undefined) {
    return payload[FormId.VEHICLE_OWNER_PERSON_DATA][
      PersonComponentId.PERSON_DATA_LACK_OF_PESEL
    ]
      ? ""
      : payload[FormId.VEHICLE_OWNER_PERSON_DATA][PersonComponentId.PESEL];
  }
  return "";
};

const vehicleOwnerSlice = createGenericUISlice({
  name: "@OC/vehicle/vehicleOwner",
  initialState: initialState as GenericState<FormDataInputs>,
  reducers: {
    setData: (state, action: PayloadAction<FormDataInputs>) => {
      state.data[FormId.VEHICLE_OWNER_VEHICLE] = {
        ...initialState.data[FormId.VEHICLE_OWNER_VEHICLE],
        ...action.payload[FormId.VEHICLE_OWNER_VEHICLE],
      };
      state.data[FormId.VEHICLE_OWNER_OWNER] = {
        ...initialState.data[FormId.VEHICLE_OWNER_OWNER],
        ...action.payload[FormId.VEHICLE_OWNER_OWNER],
      };
      state.data[FormId.VEHICLE_OWNER_COMPANY_DATA] = {
        ...initialState.data[FormId.VEHICLE_OWNER_COMPANY_DATA],
        ...action.payload[FormId.VEHICLE_OWNER_COMPANY_DATA],
      };

      state.data[FormId.VEHICLE_OWNER_PERSON_DATA] = {
        ...initialState.data[FormId.VEHICLE_OWNER_PERSON_DATA],
        ...action.payload[FormId.VEHICLE_OWNER_PERSON_DATA],
        [PersonComponentId.PESEL]: resetPeselData(action.payload),
      };

      state.data[FormId.VEHICLE_OWNER_CONTACT_PERSON_DATA] = {
        ...initialState.data[FormId.VEHICLE_OWNER_CONTACT_PERSON_DATA],
        ...action.payload[FormId.VEHICLE_OWNER_CONTACT_PERSON_DATA],
      };
      state.ui.validated = true;
    },
    setVehicleType: (state, action: PayloadAction<string>) => {
      state.data[FormId.VEHICLE_OWNER_VEHICLE][
        VehicleComponentId.VEHICLE_TYPE
      ] = action.payload;
    },
    setOwnerType: (state, action: PayloadAction<string>) => {
      state.data[FormId.VEHICLE_OWNER_OWNER][OwnerComponentId.OWNER_TYPE] =
        action.payload;
      state.ui["ownerPerson[country]"] =
        state.data[FormId.VEHICLE_OWNER_PERSON_DATA][
          PersonComponentId.COUNTRY
        ].value;
      state.ui["ownerCompany[country]"] =
        state.data[FormId.VEHICLE_OWNER_COMPANY_DATA][
          CompanyComponentId.COUNTRY
        ].value;
    },
    setContactPersonType: (state, action: PayloadAction<string>) => {
      state.data[FormId.VEHICLE_OWNER_CONTACT_PERSON_DATA][
        ContactPersonComponentId.PERSON_TYPE
      ] = action.payload;
    },
    setLackOfPesel: (state, action: PayloadAction<string>) => {
      state.data[FormId.VEHICLE_OWNER_PERSON_DATA][
        PersonComponentId.PERSON_DATA_LACK_OF_PESEL
      ] = action.payload;
    },
    setAvailableCities: (
      state,
      action: PayloadAction<{ label: string; value: string }[]>
    ) => {
      state.ui["vehicleOwnerAvailableCities"] = action.payload;
    },
    setAvailableModels: (
      state,
      action: PayloadAction<{ label: string; value: string }[]>
    ) => {
      state.ui["vehicleOwnerAvailableModels"] = action.payload;
    },
    //JIRA LPPLWEBMOB-495
    setApplicantIsOwner: (state, action: PayloadAction<boolean>) => {
      state.data[FormId.VEHICLE_OWNER_PERSON_DATA][
        PersonComponentId.APPLICANT_IS_OWNER
      ] = action.payload;
      if (action.payload) {
        DownloadApplicantDataFromStorageExport();
        const VarApplicantName = localStorage.getItem("ApplicantName");
        const VarApplicantSurname = localStorage.getItem("ApplicantSurname");
        const VarApplicantPhone = localStorage.getItem("ApplicantPhone");
        const VarApplicantEmail = localStorage.getItem("ApplicantEmail");
        if (VarApplicantName) {
          state.data[FormId.VEHICLE_OWNER_PERSON_DATA][
            PersonComponentId.NAME
          ] = VarApplicantName;
        }
        if (VarApplicantSurname) {
          state.data[FormId.VEHICLE_OWNER_PERSON_DATA][
            PersonComponentId.SURNAME
          ] = VarApplicantSurname;
        }
        if (VarApplicantPhone) {
          state.data[FormId.VEHICLE_OWNER_PERSON_DATA][
            PersonComponentId.PHONE
          ] = VarApplicantPhone;
        }
        if (VarApplicantEmail) {
          state.data[FormId.VEHICLE_OWNER_PERSON_DATA][
            PersonComponentId.EMAIL
          ] = VarApplicantEmail;
        }
      } else {
        ResetApplicantIsOwner();
        const EmptyString = "";
        const EmptyPhone = "+48";
        state.data[FormId.VEHICLE_OWNER_PERSON_DATA][
          PersonComponentId.NAME
        ] = EmptyString;
        state.data[FormId.VEHICLE_OWNER_PERSON_DATA][
          PersonComponentId.SURNAME
        ] = EmptyString;
        state.data[FormId.VEHICLE_OWNER_PERSON_DATA][
          PersonComponentId.PHONE
        ] = EmptyPhone;
        state.data[FormId.VEHICLE_OWNER_PERSON_DATA][
          PersonComponentId.EMAIL
        ] = EmptyString;
      }
    },
  },
});
//JIRA LPPLWEBMOB-495
export const {
  setData: setVehicleOwnerFormData,
  setOwnerType,
  setVehicleType,
  setContactPersonType,
  setUIField,
  setUIObjectField,
  setLackOfPesel,
  setApplicantIsOwner,
  setAvailableCities,
  setAvailableModels,
} = vehicleOwnerSlice.actions;

export const { reducer } = vehicleOwnerSlice;
